import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Business,
  Person,
  Lock,
  Visibility,
  VisibilityOff,
  Search,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import api from '../api';
import { getStatusColor } from '../utils';
import { statusTextPT } from '../i18n';

interface LoginForm {
  partnerCode: string;
  password: string;
}

interface ReferredClient {
  _id: string;
  name: string;
  trackingCode: string;
  status: string;
  consultingFee: number;
  createdAt: string;
  type: string;
  modality: string;
}

interface PartnerData {
  partnerCode: string;
  name: string;
  referredFee: number;
  clients: ReferredClient[];
}

interface PartnerLoginResponse {
  partner: {
    _id: string;
    name: string;
    partnerCode: string;
    referralLink: string;
  };
  sessionToken: string;
}

const loginSchema = yup.object({
  partnerCode: yup.string().required('Código do parceiro é obrigatório'),
  password: yup.string().required('Senha é obrigatória'),
});

export default function PartnerTrackingPage() {
  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partnerData, setPartnerData] = useState<PartnerData | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      partnerCode: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Login and get session token
      const loginResponse = await api.post('/referrals/partner/login', {
        partnerCode: data.partnerCode,
        password: data.password,
      });

      const loginData: PartnerLoginResponse = loginResponse.data.data;
      setSessionToken(loginData.sessionToken);

      // Step 2: Fetch referred clients data
      const clientsResponse = await api.get(`/referrals/partner/${loginData.partner._id}/clients`, {
        headers: {
          'Authorization': `Bearer ${loginData.sessionToken}`
        }
      });

      const clientsData = clientsResponse.data.data;
      
      // Combine partner info with clients data
      const completePartnerData: PartnerData = {
        partnerCode: clientsData.partner.partnerCode,
        name: clientsData.partner.name,
        referredFee: clientsData.partner.referredFee,
        clients: clientsData.clients.map((client: any) => ({
          _id: client.trackingCode, // Use tracking code as ID for display
          name: client.name,
          trackingCode: client.trackingCode,
          status: client.status,
          consultingFee: client.consultingFee,
          createdAt: client.registrationDate,
          type: 'N/A', // Not provided by API
          modality: 'N/A', // Not provided by API
        }))
      };

      setPartnerData(completePartnerData);
      setLoggedIn(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao fazer login ou carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setLoggedIn(false);
    setPartnerData(null);
    setSessionToken(null);
    setError(null);
  };



  if (!loggedIn) {
    return (
      <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
        <Container maxWidth="sm">
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Portal do Parceiro
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Acompanhe o status dos seus clientes indicados
                </Typography>
              </Box>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                  <Controller
                    name="partnerCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Código do Parceiro"
                        placeholder="PTR-XXXXXXXXXXXX"
                        error={!!errors.partnerCode}
                        helperText={errors.partnerCode?.message}
                        required
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person />
                              </InputAdornment>
                            ),
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-input': {
                            textTransform: 'uppercase',
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Senha"
                        type={showPassword ? 'text' : 'password'}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        required
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button
                                  onClick={() => setShowPassword(!showPassword)}
                                  sx={{ minWidth: 'auto', p: 1 }}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </Button>
                              </InputAdornment>
                            ),
                          }
                        }}
                      />
                    )}
                  />

                  {error && (
                    <Alert severity="error">
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                    sx={{
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  >
                    {loading ? 'Entrando...' : 'Entrar'}
                  </Button>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Business sx={{ fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Portal do Parceiro
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Bem-vindo, {partnerData?.name}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              onClick={handleLogout}
              sx={{ textTransform: 'none' }}
            >
              Sair
            </Button>
          </Box>

          {/* Partner Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Resumo da Conta
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Código do Parceiro
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                    {partnerData?.partnerCode}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de Indicação
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    €{partnerData?.referredFee}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total de Indicações
                  </Typography>
                  <Typography variant="h6">
                    {partnerData?.clients.length || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Referred Clients Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Clientes Indicados
              </Typography>

              {partnerData?.clients.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    Você ainda não possui clientes indicados
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Taxa</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {partnerData?.clients.map((client) => (
                        <TableRow key={client._id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                              {client.trackingCode}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500 }}>
                              {client.name}
                            </Typography>
                          </TableCell>
                                                     <TableCell>
                             <Chip
                               label={statusTextPT[client.status] || client.status}
                               color={getStatusColor(client.status) as any}
                               size="small"
                             />
                           </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                              €{client.consultingFee}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {new Date(client.createdAt).toLocaleDateString('pt-BR')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
} 